import React, { useState, useRef } from 'react';
import Header from '@/components/shared/Header';
import BottomNav from '@/components/shared/BottomNav';
import LoadingSpinner from '@/components/shared/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useAppContext } from '@/context/AppContext';
import { validateCategoryName, sanitiseString } from '@/utils/validators';
import {
  bulkRecategorize,
  bulkReclassifyPaymentMode,
  findDuplicates,
  deleteExpense,
  addExpense,
  checkDuplicate,
} from '@/services/expenseService';
import { bulkRelabelPlanned } from '@/services/plannedExpenseService';

const ADMIN_EMAIL = 'arunmailer@gmail.com';

const CATEGORY_COLORS = [
  '#1565C0','#2E7D32','#E65100','#6A1B9A','#00695C',
  '#B71C1C','#0288D1','#EF6C00','#EC407A','#F06292',
];

export default function Settings() {
  const { user, logout } = useAuth();
  const {
    categories, addCategory, removeCategory,
    paymentModes, addPaymentMode, removePaymentMode,
    plannedLabels, addPlannedLabel, removePlannedLabel,
    showToast,
  } = useAppContext();

  const isAdmin = user?.email === ADMIN_EMAIL;

  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');
  const [newCatName, setNewCatName]   = useState('');
  const [newCatEmoji, setNewCatEmoji] = useState('🔖');
  const [newCatColor, setNewCatColor] = useState(CATEGORY_COLORS[0]);
  const [newMode, setNewMode]         = useState('');
  const [newPlannedLabel, setNewPlannedLabel] = useState('');
  const [newPlannedEmoji, setNewPlannedEmoji] = useState('🔖');
  const [signingOut, setSigningOut]   = useState(false);

  // Duplicate detection
  const [duplicates, setDuplicates]   = useState(null);
  const [loadingDupes, setLoadingDupes] = useState(false);

  // CSV upload
  const csvInputRef = useRef(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const [csvResult, setCsvResult]       = useState(null);

  function toggleDarkMode() {
    const next = !darkMode;
    setDarkMode(next);
    localStorage.setItem('darkMode', String(next));
    document.documentElement.classList.toggle('dark', next);
  }

  function handleAddCategory() {
    const { valid, error } = validateCategoryName(newCatName);
    if (!valid) { showToast(error, 'error'); return; }
    const id = sanitiseString(newCatName).toLowerCase().replace(/\s+/g, '_');
    if (categories.find(c => c.id === id)) {
      showToast('Category already exists', 'error'); return;
    }
    addCategory({ id, label: sanitiseString(newCatName), emoji: newCatEmoji, color: newCatColor });
    setNewCatName('');
    showToast('Category added ✓', 'success');
  }

  async function handleDeleteCategory(cat) {
    if (cat.id === 'others') { showToast("'Others' category cannot be deleted", 'error'); return; }
    if (!confirm(`Delete "${cat.label}"? All past expenses will be moved to "Others".`)) return;
    try {
      const count = await bulkRecategorize(cat.id, 'others');
      removeCategory(cat.id);
      showToast(`Deleted "${cat.label}". ${count} expense${count !== 1 ? 's' : ''} moved to Others.`, 'success');
    } catch {
      showToast('Failed to delete category', 'error');
    }
  }

  function handleAddMode() {
    const label = sanitiseString(newMode);
    if (!label) { showToast('Payment mode name is required', 'error'); return; }
    const id = label.toLowerCase().replace(/\s+/g, '_');
    if (paymentModes.find(m => m.id === id)) {
      showToast('Mode already exists', 'error'); return;
    }
    addPaymentMode({ id, label, color: '#78909C' });
    setNewMode('');
    showToast('Payment mode added ✓', 'success');
  }

  async function handleDeleteMode(mode) {
    if (mode.id === 'cash') { showToast("'Cash' payment mode cannot be deleted", 'error'); return; }
    if (!confirm(`Delete "${mode.label}"? All past expenses will be changed to "Cash".`)) return;
    try {
      const count = await bulkReclassifyPaymentMode(mode.id, 'cash');
      removePaymentMode(mode.id);
      showToast(`Deleted "${mode.label}". ${count} expense${count !== 1 ? 's' : ''} moved to Cash.`, 'success');
    } catch {
      showToast('Failed to delete payment mode', 'error');
    }
  }

  async function handleFindDuplicates() {
    setLoadingDupes(true);
    setDuplicates(null);
    try {
      const dupes = await findDuplicates();
      setDuplicates(dupes);
      if (dupes.length === 0) showToast('No duplicates found ✓', 'success');
    } catch {
      showToast('Failed to scan for duplicates', 'error');
    } finally {
      setLoadingDupes(false);
    }
  }

  async function handleDeleteDuplicate(id) {
    if (!confirm('Delete this duplicate entry?')) return;
    try {
      await deleteExpense(id);
      setDuplicates(prev => prev.filter(d => d.id !== id));
      showToast('Duplicate deleted ✓', 'success');
    } catch {
      showToast('Failed to delete', 'error');
    }
  }

  // CSV upload — parse and bulk-add expenses (admin only)
  async function handleCsvUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setCsvResult(null);
    try {
      const text = await file.text();
      const lines = text.trim().split('\n').filter(Boolean);
      // Skip header row
      const dataLines = lines.slice(1);
      let added = 0;
      let skipped = 0;
      const errors = [];
      for (const line of dataLines) {
        // Parse CSV columns: Date, Vendor, Category, Payment Mode, Amount (₹), Notes
        const cols = line.split(',').map(c => c.replace(/^"|"$/g, '').replace(/""/g, '"').trim());
        const [date, vendor, rawCategory, rawMode, amountStr, comment = ''] = cols;
        const amount = Number(String(amountStr).replace(/[^\d.]/g, ''));
        if (!date || !vendor || !rawCategory || !rawMode || isNaN(amount) || amount <= 0) {
          errors.push(`Skipped invalid row: ${line.slice(0, 60)}`);
          skipped++;
          continue;
        }
        // Normalize category: match by id or label (case-insensitive)
        const catMatch = categories.find(
          c => c.id === rawCategory || c.label.toLowerCase() === rawCategory.toLowerCase()
        );
        const category = catMatch?.id ?? rawCategory.toLowerCase().replace(/\s+/g, '_');

        // Normalize payment mode: match by id or label (case-insensitive)
        const modeMatch = paymentModes.find(
          m => m.id === rawMode || m.label.toLowerCase() === rawMode.toLowerCase()
        );
        const paymentMode = modeMatch?.id ?? rawMode.toLowerCase().replace(/\s+/g, '_');
        // Check for duplicate before inserting
        const dupe = await checkDuplicate({ date, category, paymentMode, amount });
        if (dupe) { skipped++; continue; }
        await addExpense({ date, vendor, amount, category, paymentMode, comment });
        added++;
      }
      setCsvResult({ added, skipped, errors });
      if (added > 0) showToast(`Imported ${added} expense${added !== 1 ? 's' : ''} ✓`, 'success');
      else showToast('No new records were imported', 'info');
    } catch (err) {
      showToast('Failed to parse CSV file', 'error');
    } finally {
      setCsvUploading(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  }

  function handleAddPlannedLabel() {
    const label = newPlannedLabel.trim();
    if (!label) { showToast('Label name is required', 'error'); return; }
    const id = label.toLowerCase().replace(/\s+/g, '_').replace(/[^\w]/g, '');
    if (plannedLabels.find(l => l.id === id)) {
      showToast('Label already exists', 'error'); return;
    }
    addPlannedLabel({ id, label: `${newPlannedEmoji} ${label}` });
    setNewPlannedLabel('');
    showToast('Label added ✓', 'success');
  }

  async function handleDeletePlannedLabel(lbl) {
    if (lbl.id === 'big_purchase') {
      showToast("'Big Purchase' label cannot be deleted", 'error'); return;
    }
    if (!confirm(`Delete "${lbl.label}"? All planned expenses with this label will be moved to "Big Purchase".`)) return;
    try {
      const count = await bulkRelabelPlanned(lbl.id, 'big_purchase');
      removePlannedLabel(lbl.id);
      showToast(`Deleted "${lbl.label}". ${count} expense${count !== 1 ? 's' : ''} moved to Big Purchase.`, 'success');
    } catch {
      showToast('Failed to delete label', 'error');
    }
  }

  async function handleSignOut() {
    if (!confirm('Sign out?')) return;
    setSigningOut(true);
    await logout();
  }

  return (
    <div className="flex flex-col h-full">
      <Header title="⚙ Settings" bgColor="bg-gray-700" />

      <div className="flex-1 overflow-y-auto scrollbar-none pb-20 space-y-5 p-4">

        {/* Profile */}
        <div className="card flex items-center gap-4">
          {user?.photoURL ? (
            <img src={user.photoURL} alt={user.displayName} className="w-12 h-12 rounded-full" referrerPolicy="no-referrer" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center text-xl font-bold">
              {user?.displayName?.[0] ?? '?'}
            </div>
          )}
          <div>
            <p className="text-sm font-bold text-gray-800">{user?.displayName}</p>
            <p className="text-xs text-gray-500">{user?.email}</p>
            {isAdmin && <p className="text-xs text-green-600 font-semibold mt-0.5">● Admin</p>}
          </div>
        </div>

        {/* Dark mode toggle */}
        <div className="card flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-gray-700">🌙 Dark Mode</p>
            <p className="text-xs text-gray-500">Switch between light and dark theme</p>
          </div>
          <button
            onClick={toggleDarkMode}
            className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors ${
              darkMode ? 'bg-primary' : 'bg-gray-300'
            }`}
            role="switch"
            aria-checked={darkMode}
            aria-label="Toggle dark mode"
          >
            <span
              className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                darkMode ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {/* Category management */}
        <div className="card space-y-3">
          <h2 className="text-sm font-bold text-gray-700">Categories</h2>
          <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
            {categories.map(c => (
              <span key={c.id}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-chip text-xs font-medium"
                style={{ background: `${c.color}22`, color: c.color }}>
                {c.emoji} {c.label}
                {isAdmin && c.id !== 'others' && (
                  <button
                    onClick={() => handleDeleteCategory(c)}
                    className="ml-1 text-red-400 hover:text-red-600 font-bold leading-none"
                    aria-label={`Delete ${c.label}`}
                    title="Delete category"
                  >×</button>
                )}
              </span>
            ))}
          </div>

          {isAdmin && (
            <>
              <div className="flex gap-2">
                <input value={newCatEmoji} onChange={e => setNewCatEmoji(e.target.value)}
                  className="input-field w-12 text-center" maxLength={2} aria-label="Category emoji" />
                <input value={newCatName} onChange={e => setNewCatName(e.target.value)}
                  placeholder="Category name" className="input-field flex-1" aria-label="Category name" />
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {CATEGORY_COLORS.map(c => (
                  <button key={c} onClick={() => setNewCatColor(c)}
                    className={`w-6 h-6 rounded-full transition-all ${newCatColor === c ? 'ring-2 ring-offset-1 ring-gray-600' : ''}`}
                    style={{ background: c }} aria-label={`Color ${c}`} />
                ))}
              </div>
              <button onClick={handleAddCategory} className="btn-secondary w-full text-sm">
                ＋ Add Category
              </button>
            </>
          )}
        </div>

        {/* Payment modes */}
        <div className="card space-y-3">
          <h2 className="text-sm font-bold text-gray-700">Payment Modes</h2>
          <div className="flex flex-wrap gap-1.5">
            {paymentModes.map(m => (
              <span key={m.id} className="chip inline-flex items-center gap-1">
                {m.label}
                {isAdmin && m.id !== 'cash' && (
                  <button
                    onClick={() => handleDeleteMode(m)}
                    className="ml-1 text-red-400 hover:text-red-600 font-bold leading-none"
                    aria-label={`Delete ${m.label}`}
                    title="Delete payment mode"
                  >×</button>
                )}
              </span>
            ))}
          </div>

          {isAdmin && (
            <div className="flex gap-2">
              <input value={newMode} onChange={e => setNewMode(e.target.value)}
                placeholder="New payment mode (e.g. SBI)" className="input-field flex-1"
                aria-label="New payment mode" />
              <button onClick={handleAddMode} className="btn-secondary px-3 text-sm">Add</button>
            </div>
          )}
        </div>

        {/* Planned Labels */}
        <div className="card space-y-3">
          <h2 className="text-sm font-bold text-gray-700">Planned Expense Labels</h2>
          <div className="flex flex-wrap gap-1.5">
            {plannedLabels.map(l => (
              <span key={l.id} className="chip inline-flex items-center gap-1">
                {l.label}
                {l.id !== 'big_purchase' && (
                  <button
                    onClick={() => handleDeletePlannedLabel(l)}
                    className="ml-1 text-red-400 hover:text-red-600 font-bold leading-none"
                    aria-label={`Delete ${l.label}`}
                    title="Delete label"
                  >×</button>
                )}
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={newPlannedEmoji} onChange={e => setNewPlannedEmoji(e.target.value)}
              className="input-field w-12 text-center" maxLength={2} aria-label="Label emoji" />
            <input value={newPlannedLabel} onChange={e => setNewPlannedLabel(e.target.value)}
              placeholder="New label (e.g. Insurance)" className="input-field flex-1"
              aria-label="New planned label" />
            <button onClick={handleAddPlannedLabel} className="btn-secondary px-3 text-sm">Add</button>
          </div>
        </div>

        {/* Duplicate detection (admin only) */}
        {isAdmin && (
          <div className="card space-y-3">
            <h2 className="text-sm font-bold text-gray-700">Duplicate Detection</h2>
            <p className="text-xs text-gray-500">Find expenses with same date, category, payment mode and amount.</p>
            <button
              onClick={handleFindDuplicates}
              disabled={loadingDupes}
              className="btn-secondary w-full text-sm disabled:opacity-60"
            >
              {loadingDupes ? 'Scanning…' : '🔍 Scan for Duplicates'}
            </button>

            {duplicates !== null && duplicates.length === 0 && (
              <p className="text-xs text-green-600 font-semibold text-center">✓ No duplicates found</p>
            )}

            {duplicates && duplicates.length > 0 && (
              <div className="space-y-2">
                <p className="text-xs text-red-600 font-semibold">{duplicates.length} duplicate record{duplicates.length !== 1 ? 's' : ''} found:</p>
                {duplicates.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                    <div className="text-xs">
                      <p className="font-semibold text-gray-800">{d.date} · {d.vendor}</p>
                      <p className="text-gray-500">{d.category} · {d.paymentMode} · ₹{d.amount}</p>
                    </div>
                    <button
                      onClick={() => handleDeleteDuplicate(d.id)}
                      className="text-xs bg-red-500 text-white px-2 py-1 rounded font-semibold hover:bg-red-600"
                    >Delete</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* CSV Upload (admin only) */}
        {isAdmin && (
          <div className="card space-y-3">
            <h2 className="text-sm font-bold text-gray-700">Import Expenses (CSV)</h2>
            <p className="text-xs text-gray-500">
              Upload a CSV file in the same format as the download (Date, Vendor, Category, Payment Mode, Amount, Notes).
              Duplicate entries are skipped automatically.
            </p>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvUpload}
              disabled={csvUploading}
              className="text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded file:border-0
                         file:text-xs file:font-semibold file:bg-primary file:text-white
                         hover:file:bg-blue-700 disabled:opacity-60 w-full"
              aria-label="Upload CSV file"
            />
            {csvUploading && <p className="text-xs text-gray-500">Importing…</p>}
            {csvResult && (
              <div className="text-xs space-y-1">
                <p className="text-green-600 font-semibold">✓ Added: {csvResult.added} · Skipped: {csvResult.skipped}</p>
                {csvResult.errors.map((e, i) => (
                  <p key={i} className="text-red-500">{e}</p>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sign out */}
        <div className="card">
          <h2 className="text-sm font-bold text-gray-700 mb-3">Account</h2>
          <button
            onClick={handleSignOut}
            disabled={signingOut}
            className="w-full py-2.5 text-sm font-semibold text-danger border border-danger
                       rounded-input hover:bg-red-50 transition-colors disabled:opacity-60"
          >
            {signingOut ? 'Signing out…' : '⬅ Sign Out'}
          </button>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
